import React, { useState, useEffect } from 'react';
import { requestNotificationPermission } from '../notifications';
import { useAuth } from '../AuthContext';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = window.navigator.standalone === true;

export default function NotificationPrompt() {
  const { currentUser } = useAuth();
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    // On iOS, push only works when installed to home screen (iOS 16.4+).
    // Show an install nudge instead of silently hiding.
    if (isIOS && !isStandalone) {
      const t = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(t);
    }
    // Show prompt if notifications not yet decided and browser supports it
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      const t = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  if (!show) return null;

  async function enable() {
    setStatus('requesting');
    const token = await requestNotificationPermission(
      currentUser?.uid,
      currentUser?.email
    );
    setStatus(token ? 'granted' : 'denied');
    setTimeout(() => setShow(false), 2000);
  }

  const cardStyle = {
    position: 'fixed', bottom: 80, left: 16, right: 16,
    background: 'white', borderRadius: 14,
    padding: '16px 18px', zIndex: 200,
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    border: '1.5px solid var(--green-pale)',
    maxWidth: 440, margin: '0 auto',
  };

  // iOS Safari without home screen install — show install nudge instead
  if (isIOS && !isStandalone) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 24 }}>📲</span>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>Add to your home screen</div>
            <div style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
              Tap the share button then <strong>Add to Home Screen</strong> to get notifications about your jobs on iPhone.
            </div>
          </div>
        </div>
        <button className="btn-secondary" onClick={() => setShow(false)} style={{ width: '100%', padding: '11px' }}>
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {status === 'granted' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>✅</span>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--green-dark)' }}>Notifications enabled</div>
            <div style={{ fontSize: '0.95rem', color: 'var(--text-muted)' }}>You'll be notified about your jobs</div>
          </div>
        </div>
      ) : status === 'denied' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🔕</span>
          <div style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>Notifications blocked. You can enable them in your browser settings.</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 24 }}>🔔</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Stay updated</div>
              <div style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                Get notified when Dean responds to your quote, sends an invoice or messages you.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-primary"
              onClick={enable}
              disabled={status === 'requesting'}
              style={{ flex: 2, padding: '11px' }}
            >
              {status === 'requesting' ? 'Enabling...' : 'Enable notifications'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShow(false)}
              style={{ flex: 1, padding: '11px' }}
            >
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
